import { getDecryptedValue, getEncryptedValue } from '../helpers/cryptoHelper';
import { SESSION_DATA } from '../helpers/storeHelper';
import { EXCLUSIVE_SECURE_SESSION_STORAGE_KEYS } from './constants';

const isSessionStorageKeySecureExclusive = (key: string) => {
  return EXCLUSIVE_SECURE_SESSION_STORAGE_KEYS?.includes(key);
};

const clearSessionStorage = () => {
  window.sessionStorage.clear();
};

const clearPickYourPlanSessionStorageItems = () => {
  removeMultipleFromStorage(
    Object.keys(SESSION_DATA).filter(
      (key) =>
        key !== SESSION_DATA.ORIGIN &&
        key !== SESSION_DATA.ACCESS_TOKEN_EXPIRY_TIME &&
        key !== SESSION_DATA.ACCESS_TOKEN &&
        key !== SESSION_DATA.REFRESH_TOKEN &&
        key !== SESSION_DATA.USER_NAME &&
        key !== SESSION_DATA.USER_TYPE &&
        key !== SESSION_DATA.ID_TOKEN &&
        key !== SESSION_DATA.RETAIL_GOVT_DETAILS,
    ),
  );
};

const clearActiveUserSessionStorageItems = () => {
  removeMultipleFromStorage(
    Object.keys(SESSION_DATA).filter(
      (key) =>
        key !== SESSION_DATA.ORIGIN &&
        key !== SESSION_DATA.ACCESS_TOKEN_EXPIRY_TIME &&
        key !== SESSION_DATA.ACCESS_TOKEN &&
        key !== SESSION_DATA.REFRESH_TOKEN &&
        key !== SESSION_DATA.USER_NAME &&
        key !== SESSION_DATA.USER_TYPE &&
        key !== SESSION_DATA.SELECTED_TAB &&
        key !== SESSION_DATA.ID_TOKEN &&
        key !== SESSION_DATA.MEMBER_SUBSCRIBER_DETAILS &&
        key !== SESSION_DATA.MEMBER_ID,
    ),
  );
};

const clearBinderUserSessionStorageItems = () => {
  removeMultipleFromStorage(
    Object.keys(SESSION_DATA).filter(
      (key) =>
        key !== SESSION_DATA.ORIGIN &&
        key !== SESSION_DATA.USER_TYPE &&
        key !== SESSION_DATA.BINDER_PAYLATER_LOGIN_DETAILS &&
        key !== SESSION_DATA.BINDER_PAYMENT_DETAILS &&
        key !== SESSION_DATA.IS_BROKER_USER &&
        key !== SESSION_DATA.IS_NMA_USER,
    ),
  );
};

const clearBinderPayNowSessionStorageForErrorScreen = () => {
  removeMultipleFromStorage(
    Object.keys(SESSION_DATA).filter(
      (key) =>
        key !== SESSION_DATA.ORIGIN &&
        key !== SESSION_DATA.USER_TYPE &&
        key !== SESSION_DATA.BINDER_PAY_NOW_MEMBER_DETAILS,
    ),
  );
};

const clearBinderPayLaterSessionStorageForErrorScreen = () => {
  removeMultipleFromStorage(
    Object.keys(SESSION_DATA).filter(
      (key) =>
        key !== SESSION_DATA.ORIGIN &&
        key !== SESSION_DATA.USER_TYPE &&
        key !== SESSION_DATA.BINDER_PAYLATER_LOGIN_DETAILS,
    ),
  );
};

const clearGuestSessionStorageForErrorScreen = () => {
  removeMultipleFromStorage(
    Object.keys(SESSION_DATA).filter(
      (key) =>
        key !== SESSION_DATA.ORIGIN &&
        key !== SESSION_DATA.USER_TYPE &&
        key !== SESSION_DATA.MEMBER_SUBSCRIBER_DETAILS &&
        key !== SESSION_DATA.MEMBER_ID,
    ),
  );
};

const clearPaymentDetailsFromSession = () => {
  removeMultipleFromStorage(
    Object.keys(SESSION_DATA).filter(
      (key) =>
        key !== SESSION_DATA.MEMBER_ID &&
        key !== SESSION_DATA.MPP_ID &&
        key !== SESSION_DATA.ACCESS_TOKEN_EXPIRY_TIME &&
        key !== SESSION_DATA.ACCESS_TOKEN &&
        key !== SESSION_DATA.ID_TOKEN &&
        key !== SESSION_DATA.REFRESH_TOKEN &&
        key !== SESSION_DATA.USER_NAME &&
        key !== SESSION_DATA.USER_TYPE &&
        key !== SESSION_DATA.ORIGIN &&
        key !== SESSION_DATA.M_ID &&
        key !== SESSION_DATA.MEMBER_SUBSCRIBER_DETAILS &&
        key !== SESSION_DATA.IS_BROKER_USER &&
        key !== SESSION_DATA.IS_NMA_USER,
    ),
  );
};

const clearSelectedPaymentDetailsFromSession = () => {
  removeMultipleFromStorage(
    Object.keys(SESSION_DATA).filter(
      (key) =>
        key !== SESSION_DATA.MEMBER_ID &&
        key !== SESSION_DATA.MPP_ID &&
        key !== SESSION_DATA.SELECTED_CASE &&
        key !== SESSION_DATA.SELECTED_CIM &&
        key !== SESSION_DATA.ACCESS_TOKEN_EXPIRY_TIME &&
        key !== SESSION_DATA.ACCESS_TOKEN &&
        key !== SESSION_DATA.ID_TOKEN &&
        key !== SESSION_DATA.REFRESH_TOKEN &&
        key !== SESSION_DATA.USER_NAME &&
        key !== SESSION_DATA.ORIGIN &&
        key !== SESSION_DATA.USER_TYPE &&
        key !== SESSION_DATA.M_ID &&
        key !== SESSION_DATA.ORIGIN &&
        key !== SESSION_DATA.POLICY_ID &&
        key !== SESSION_DATA.RETAIL_GOVT_DETAILS &&
        key !== SESSION_DATA.IS_NMA_USER &&
        key !== SESSION_DATA.IS_BROKER_USER,
    ),
  );
};

const getFromSessionStorage = (key: string) => {
  const result = window.sessionStorage.getItem(key);
  if (!!result && result !== 'undefined') {
    if (isSessionStorageKeySecureExclusive(key)) {
      return result;
    } else {
      return getDecryptedValue(result);
    }
  }
  return undefined;
};

const removeMultipleFromStorage = (keys: string[]) => {
  keys.map((key) => removeFromStorage(key));
};

const removeFromStorage = (key: string) => {
  window.sessionStorage.removeItem(key);
};

function getEncryptedValueForSessionStorage(value: any) {
  return getEncryptedValue(value.toString()).toString();
}

function setSessionStorageKeyAndValue(key: string, value: any) {
  window.sessionStorage.setItem(key, value);
}

/**
 * set session storage key and value by checking type of value and secure status of key
 * @param key - session storage key
 * @param value - session storage value
 */

function setToSessionStorage(key: string, value: any) {
  // check store value type
  let storeValue = value;
  storeValue = typeof value === 'object' ? JSON.stringify(value) : value;

  // check store key secure status
  if (isSessionStorageKeySecureExclusive(key)) {
    setSessionStorageKeyAndValue(key, storeValue);
  } else {
    setSessionStorageKeyAndValue(
      key,
      getEncryptedValueForSessionStorage(storeValue),
    );
  }

  // Dispatch custom event to notify listeners of storage changes
  const event = new CustomEvent('sessionStorageUpdated', {
    detail: { key, value },
  });
  window.dispatchEvent(event);
}

const setMultipleToSessionStorage = (
  sessionKeys: { key: string; value: any }[],
) => {
  sessionKeys.map(({ key, value }) => setToSessionStorage(key, value));
};

export {
  clearSessionStorage,
  removeFromStorage,
  getFromSessionStorage,
  setToSessionStorage,
  clearPaymentDetailsFromSession,
  clearSelectedPaymentDetailsFromSession,
  setMultipleToSessionStorage,
  clearActiveUserSessionStorageItems,
  clearBinderUserSessionStorageItems,
  clearBinderPayNowSessionStorageForErrorScreen,
  clearBinderPayLaterSessionStorageForErrorScreen,
  clearGuestSessionStorageForErrorScreen,
  clearPickYourPlanSessionStorageItems,
};
